import {
	Fragment,
	createContext,
	forwardRef,
	useContext,
	useMemo
} from 'react';

import * as SelectPrimitive from '@radix-ui/react-select';
import { cva } from 'class-variance-authority';
import { Link } from 'react-router-dom';

import ChevronDown from '@/icons/chevron-down.svg';
import { cn } from '@/utils';

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectContext = createContext({});

const iconVariants = cva('', {
	variants: {
		size: {
			default: 'h-6 w-6',
			sm: 'h-[10px] w-[10px]',
			xs: 'h-2 w-2'
		}
	},
	defaultVariants: {
		size: 'default'
	}
});

const selectVariants = cva('', {
	variants: {
		size: {
			default: 'h-10 px-3',
			xl: 'h-12 px-3 text-sm lg:h-14 lg:px-4 lg:text-base',
			lg: 'h-10 px-3 py-2.5 text-sm lg:h-12 lg:text-base',
			sm: 'h-7 px-3 text-xs',
			xs: 'h-6 px-1 text-xs'
		},
		error: {
			true: 'border-border-red focus:border-border-red'
		}
	},
	defaultVariants: {
		size: 'default'
	}
});

function Select({ error, className, ...props }) {
	const contextValue = useMemo(() => ({ error }), [error]);
	return (
		<SelectContext.Provider value={contextValue}>
			<SelectPrimitive.Root className={className} multiple {...props} />
		</SelectContext.Provider>
	);
}

const SelectTrigger = forwardRef(
	({ className, size, children, noIcon = false, ...props }, ref) => {
		const { error } = useContext(SelectContext);

		return (
			<SelectPrimitive.Trigger
				ref={ref}
				className={cn(
					'flex h-10 w-full items-center justify-between gap-1 rounded border border-grey-200 bg-white px-3 py-2 text-sm transition-colors focus:outline-none focus-visible:border-black disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:border-black data-[placeholder]:text-gray-500 [&>span]:line-clamp-1',
					{ '!focus:border-red !border-red': error },
					className,
					selectVariants({ size })
				)}
				{...props}
			>
				{children}
				{!noIcon && (
					<SelectPrimitive.Icon asChild>
						<div
							className={cn(
								'flex items-center justify-center border-0 text-inherit',
								iconVariants({ size })
							)}
						>
							<ChevronDown />
						</div>
					</SelectPrimitive.Icon>
				)}
			</SelectPrimitive.Trigger>
		);
	}
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = forwardRef(
	(
		{
			className,
			children,
			size,
			position = 'popper',
			noPortal = false,
			...props
		},
		ref
	) => {
		const Container = noPortal ? Fragment : SelectPrimitive.Portal;

		return (
			<Container>
				<SelectPrimitive.Content
					ref={ref}
					className={cn(
						'relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-white text-black shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
						position === 'popper' &&
							'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
						className
					)}
					position={position}
					{...props}
				>
					<SelectPrimitive.Viewport
						className={cn(
							'flex flex-col gap-1 p-1',
							position === 'popper' &&
								'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
						)}
					>
						{children}
					</SelectPrimitive.Viewport>
				</SelectPrimitive.Content>
			</Container>
		);
	}
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = forwardRef(({ className, ...props }, ref) => (
	<SelectPrimitive.Label
		ref={ref}
		className={cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)}
		{...props}
	/>
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = forwardRef(({ className, children, ...props }, ref) => (
	<SelectPrimitive.Item
		ref={ref}
		className={cn(
			'data-[state=checked]:bg-lightred relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-4 pr-2 text-xs text-gray-600 outline-none transition-colors focus:bg-gray-100 focus:text-black data-[disabled]:pointer-events-none data-[state=checked]:bg-light-red data-[state=checked]:text-red data-[disabled]:opacity-50',
			className
		)}
		{...props}
	>
		<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
	</SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = forwardRef(({ className, ...props }, ref) => (
	<SelectPrimitive.Separator
		ref={ref}
		className={cn('-mx-1 my-1 h-px', className)}
		{...props}
	/>
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

const SelectItemLink = forwardRef(
	({ className, children, to, linkProps = {}, ...props }, ref) => (
		<SelectPrimitive.Item
			ref={ref}
			className={cn(
				'item focus:bg-purple data-[state=checked]:bg-lightred relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-4 pr-2 text-sm text-grey-500 outline-none transition-colors focus:text-black data-[disabled]:pointer-events-none data-[state=checked]:text-red data-[disabled]:opacity-50',
				className
			)}
			{...props}
			asChild
		>
			<Link to={to} {...linkProps}>
				<SelectPrimitive.ItemText className="item-text">
					{children}
				</SelectPrimitive.ItemText>
			</Link>
		</SelectPrimitive.Item>
	)
);
SelectItemLink.displayName = 'SelectItemLink';

export {
	Select,
	SelectGroup,
	SelectValue,
	SelectTrigger,
	SelectContent,
	SelectLabel,
	SelectItem,
	SelectSeparator,
	SelectItemLink
};
